import { useDictionaryContext } from '3-foundation/Dictionary/DictionaryContext';
import { defaultNewsLimit, getNews } from '3-foundation/News/repository';
import { usePageContext } from '3-foundation/PageContent/PageContext';
import { useCallback, useEffect, useState } from 'react';
import { UI_FilterList } from 'ui/components/2-molecules/FilterList/FilterList';
import { UI_NewsOverview } from 'ui/components/3-organisms/NewsOverview/NewsOverview';
import { UI_Container } from 'ui/components/4-habitats/Container/Container';
import { useSiteContext } from '3-foundation/Site/SiteContext';
import { useRouter } from 'next/router';

export const NewsSearchResults = ({
	serverSideNewsItems,
	tags,
	totalNewsItems,
}: {
	serverSideNewsItems: Content.NewsCatalogPageProperties['newsItems'];
	tags: Content.NewsCatalogPageProperties['filterTags'];
	totalNewsItems: Content.NewsCatalogPageProperties['totalNewsItems'];
}) => {
	const pageContext = usePageContext();
	const siteContext = useSiteContext();
	const dictionary = useDictionaryContext();
	const { locale } = useRouter();

	const [clientSideNewsItems, setClientSideNewsItems] = useState(serverSideNewsItems);
	const allNewsFilter = { name: dictionary.getNews('AllNews'), count: totalNewsItems, selected: true };
	const [filters, setFilters] = useState<{ name: string; count: number; selected?: boolean }[]>([allNewsFilter, ...tags]);
	const [fetchClientSide, setFetchClientSide] = useState(false);

	const handleFilterClick = (tag) => {
		setFetchClientSide(true);
		setFilters((prevFilters) => {
			let anySelected = false;

			const updatedFilters = prevFilters.map((filter) => {
				const isClicked = filter.name === tag;
				const selected = isClicked ? !(filter.selected ?? false) : false;

				if (selected) anySelected = true;
				return { ...filter, selected };
			});

			// If no filter ended up selected, default the first one to true
			if (!anySelected && updatedFilters.length > 0) {
				updatedFilters[0].selected = true;
			}

			setClientSideNewsItems([]); // Clear NewsResults since we will use newsItems instead
			return updatedFilters;
		});
	};

	const fetchNewsItems = useCallback(
		async ({ limit }: { limit?: number }) => {
			try {
				const searchFilters = filters?.filter((f) => f.selected).filter((sf) => sf.name !== allNewsFilter.name) || [];
				const newsData = await getNews({
					filters: {
						parentId: pageContext?.id,
						tagsSubject: searchFilters.map((sf) => sf.name),
					},
					limit,
					locale,
					siteId: siteContext?.root?.id,
				});
				setClientSideNewsItems(newsData.items);
			} catch (error) {
				console.error('Failed to fetch news:', error);
			}
		},
		[filters, pageContext?.id, allNewsFilter?.name, locale, siteContext?.root?.id],
	);
	useEffect(() => {
		if (!fetchClientSide) return;
		const allNewsFilterSelected = filters?.some((filter) => filter.name === allNewsFilter.name && filter.selected);
		if (allNewsFilterSelected) return;
		fetchNewsItems({});
	}, [allNewsFilter.name, fetchClientSide, fetchNewsItems, filters]);

	useEffect(() => {
		// Serverside newsitems updates when language is changed
		setClientSideNewsItems(serverSideNewsItems);
	}, [serverSideNewsItems]);

	const currentCount = clientSideNewsItems.length > 0 ? clientSideNewsItems.length : serverSideNewsItems.length;
	const activeFilter = filters.find((f) => f.selected) ?? allNewsFilter;

	return (
		<UI_Container width="Standard">
			<UI_FilterList filters={[...filters]} onClick={(filter) => handleFilterClick(filter)} />
			<UI_NewsOverview
				cards={clientSideNewsItems.length > 0 ? clientSideNewsItems : serverSideNewsItems}
				paginationText={currentCount < activeFilter.count ? dictionary.getSearch('LoadMore') : null}
				requestMoreCards={() => fetchNewsItems({ limit: currentCount + defaultNewsLimit })}
			/>
		</UI_Container>
	);
};
